import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import {
  BILLING_FEATURES,
  BILLING_MODULES,
  FEATURE_LABEL,
  MODULE_DESCRIPTION,
  MODULE_LABEL,
  paying,
  SUBSCRIPTION_STATUS_LABEL,
  type BillingFeature,
  type BillingModule,
} from '../lib/billing';
import { formatDate } from '../lib/format';
import type { PlatformTenant } from '../lib/types';
import { useToast } from './toast';
import { Alert, Badge, Button, Field, Input, Modal, Segmented, Switch, Textarea } from './ui';

/** How Edvora is charging a school for Edvora. */
type Arrangement = 'TRIAL' | 'COMPLIMENTARY' | 'PAID';

const ARRANGEMENTS: { value: Arrangement; label: string }[] = [
  { value: 'TRIAL', label: 'Trial' },
  { value: 'COMPLIMENTARY', label: 'Complimentary' },
  { value: 'PAID', label: 'Paid' },
];

const sameList = <T,>(a: T[], b: T[]) => a.length === b.length && a.every((item) => b.includes(item));

/** A module or add-on, ticked. */
function Tick({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange(next: boolean): void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`flex gap-2.5 rounded-lg border p-2.5 ${checked ? 'border-brand-200 bg-brand-50/50' : 'border-slate-200'} ${disabled ? 'opacity-60' : ''}`}>
      <input
        type="checkbox"
        className="mt-0.5 size-4 shrink-0 rounded border-slate-300 accent-brand-700"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-800">{label}</span>
        {description && <span className="block text-xs text-slate-500">{description}</span>}
      </span>
    </label>
  );
}

/**
 * Edvora's arrangement with a school: a trial, a complimentary plan, or what it pays for - and, for the first two,
 * exactly which modules it includes. What's ticked here is what the school has, whether or not billing is enforced.
 */
export function PlanModal({ tenant, onClose }: { tenant: PlatformTenant; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const billing = tenant.billing;
  const onTrial = !!billing?.trialEndsAt && new Date(billing.trialEndsAt) > new Date();
  // Trials end when their last day does (UTC), so the day is the date part.
  const initial = {
    arrangement: (billing?.complimentary ? 'COMPLIMENTARY' : onTrial ? 'TRIAL' : 'PAID') as Arrangement,
    trialEndsOn: billing?.trialEndsAt ? billing.trialEndsAt.slice(0, 10) : '',
    signUpTrial: billing?.trialKind === 'SIGN_UP',
    trialModules: billing?.trialModules ?? [],
    complimentaryModules: billing?.complimentaryModules ?? [],
    grantedAddOns: billing?.grantedAddOns ?? [],
    notes: billing?.notes ?? '',
    deleteIfUnpaid: billing?.deleteIfUnpaid ?? false,
  };
  const [arrangement, setArrangement] = useState<Arrangement>(initial.arrangement);
  const [trialEndsOn, setTrialEndsOn] = useState(initial.trialEndsOn);
  const [signUpTrial, setSignUpTrial] = useState(initial.signUpTrial);
  const [trialModules, setTrialModules] = useState<BillingModule[]>(initial.trialModules);
  const [compModules, setCompModules] = useState<BillingModule[]>(initial.complimentaryModules);
  const [granted, setGranted] = useState<BillingFeature[]>(initial.grantedAddOns);
  const [notes, setNotes] = useState(initial.notes);
  const [deleteIfUnpaid, setDeleteIfUnpaid] = useState(initial.deleteIfUnpaid);

  // The Tech module comes with Facilities and the add-ons, so ticking one ticks it.
  const toggle = (list: BillingModule[], module: BillingModule, on: boolean): BillingModule[] => {
    const next = on ? [...new Set([...list, module, 'PLATFORM' as BillingModule])] : list.filter((item) => item !== module);
    return next.length === 1 && next[0] === 'PLATFORM' && !on ? [] : next;
  };

  // Only what changed, so the audit log says what Edvora actually did.
  const changes = {
    ...(arrangement === 'COMPLIMENTARY'
      ? {
          ...(initial.arrangement !== 'COMPLIMENTARY' ? { complimentary: true } : {}),
          ...(!sameList(compModules, initial.complimentaryModules) ? { complimentaryModules: compModules } : {}),
        }
      : {}),
    ...(arrangement === 'TRIAL'
      ? {
          ...(initial.arrangement === 'COMPLIMENTARY' ? { complimentary: false } : {}),
          ...(trialEndsOn !== initial.trialEndsOn ? { trialEndsOn: trialEndsOn || null } : {}),
          ...(signUpTrial !== initial.signUpTrial ? { trialKind: signUpTrial ? ('SIGN_UP' as const) : ('FULL' as const) } : {}),
          ...(!signUpTrial && !sameList(trialModules, initial.trialModules) ? { trialModules } : {}),
          ...(deleteIfUnpaid !== initial.deleteIfUnpaid ? { deleteIfUnpaid } : {}),
        }
      : {}),
    ...(arrangement === 'PAID'
      ? {
          ...(initial.arrangement === 'COMPLIMENTARY' ? { complimentary: false } : {}),
          ...(!sameList(granted, initial.grantedAddOns) ? { grantedAddOns: granted } : {}),
        }
      : {}),
    ...(notes !== initial.notes ? { notes } : {}),
  };

  const save = useMutation({
    mutationFn: () => api.patch(`/platform/tenants/${tenant.id}/billing`, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform'] });
      toast.success(`Updated ${tenant.name}’s plan`);
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const status = billing?.subscriptionStatus;
  const annual = (billing?.annualAddOns ?? []).filter((addOn) => paying(addOn.status));
  const everything = (list: BillingModule[]) => list.length === 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={`${tenant.name} plan`}
      description="What Edvora includes here is what the school has, whether or not billing is enforced on this environment."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={Object.keys(changes).length === 0} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field as="div" label="How this school is paying">
          <Segmented<Arrangement> value={arrangement} onChange={setArrangement} options={ARRANGEMENTS} />
        </Field>

        {arrangement === 'TRIAL' && (
          <>
            <Field label="Free trial through" hint="The trial lasts through the end of this day. Clear it to end the trial.">
              <Input type="date" value={trialEndsOn} onChange={(event) => setTrialEndsOn(event.target.value)} className="w-48" />
            </Field>
            <Field as="div" label="The trial includes" hint={everything(trialModules) && !signUpTrial ? 'Nothing ticked: the trial includes everything.' : undefined}>
              <div className="space-y-2">
                <Tick
                  checked={signUpTrial}
                  onChange={(on) => {
                    setSignUpTrial(on);
                    if (on) setTrialModules([]);
                  }}
                  label="Default sign-up trial"
                  description="Tech and Facilities, with rosters and devices synced only on demand - what a school gets when it signs up itself"
                />
                {BILLING_MODULES.map((module) => (
                  <Tick
                    key={module}
                    checked={!signUpTrial && trialModules.includes(module)}
                    disabled={signUpTrial}
                    onChange={(on) => setTrialModules((list) => toggle(list, module, on))}
                    label={MODULE_LABEL[module]}
                    description={MODULE_DESCRIPTION[module]}
                  />
                ))}
              </div>
            </Field>
            <Switch
              label="Delete the school if it doesn’t subscribe"
              description={
                deleteIfUnpaid
                  ? `The school and its data are deleted 30 days after the trial ends${trialEndsOn ? ` (${formatDate(new Date(new Date(`${trialEndsOn}T23:59:59Z`).getTime() + 30 * 86_400_000))})` : ''}, unless it subscribes. Extend the trial to give it more time.`
                  : 'The school is kept even if it never subscribes.'
              }
              checked={deleteIfUnpaid}
              onChange={setDeleteIfUnpaid}
            />
          </>
        )}

        {arrangement === 'COMPLIMENTARY' && (
          <Field
            as="div"
            label="Edvora provides, free"
            hint={everything(compModules) ? 'Nothing ticked: the school gets everything free.' : 'A Stripe subscription, if any, keeps billing until it’s canceled in Stripe.'}
          >
            <div className="space-y-2">
              {BILLING_MODULES.map((module) => (
                <Tick
                  key={module}
                  checked={compModules.includes(module)}
                  onChange={(on) => setCompModules((list) => toggle(list, module, on))}
                  label={MODULE_LABEL[module]}
                  description={MODULE_DESCRIPTION[module]}
                />
              ))}
            </div>
          </Field>
        )}

        {arrangement === 'PAID' && (
          <>
            <Field as="div" label="What the school pays for">
              {status ? (
                <div className="rounded-lg border border-slate-200 p-3 text-sm">
                  <p className="flex flex-wrap items-center gap-2">
                    <Badge tone={paying(status) ? (status === 'PAST_DUE' ? 'amber' : 'green') : 'red'}>{SUBSCRIPTION_STATUS_LABEL[status]}</Badge>
                    <span className="text-slate-600">
                      Edvora platform
                      {billing?.currentPeriodEnd ? `, ${billing.cancelAtPeriodEnd ? 'ends' : 'renews'} ${formatDate(billing.currentPeriodEnd)}` : ''}
                    </span>
                  </p>
                  <p className="mt-1.5 text-xs text-slate-600">
                    Monthly add-ons: {billing?.addOns.length ? billing.addOns.map((feature) => FEATURE_LABEL[feature]).join(', ') : 'none'}
                  </p>
                  <p className="text-xs text-slate-600">
                    Yearly add-ons: {annual.length ? annual.map((addOn) => `${FEATURE_LABEL[addOn.feature]}${addOn.periodEnd ? ` to ${formatDate(addOn.periodEnd)}` : ''}`).join(', ') : 'none'}
                  </p>
                </div>
              ) : (
                <Alert tone="amber">No Stripe subscription yet. The school subscribes itself under Plan &amp; billing.</Alert>
              )}
            </Field>
            <Field as="div" label="Add-ons included free" hint="On top of what the school pays for.">
              <div className="grid gap-2 sm:grid-cols-2">
                {BILLING_FEATURES.map((feature) => (
                  <label key={feature} className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-slate-300 accent-brand-700"
                      checked={granted.includes(feature)}
                      onChange={(event) => setGranted((list) => (event.target.checked ? [...list, feature] : list.filter((item) => item !== feature)))}
                    />
                    {FEATURE_LABEL[feature]}
                  </label>
                ))}
              </div>
            </Field>
            {onTrial && (
              <Alert tone="blue">
                This school is still on a trial to {formatDate(billing!.trialEndsAt!)}. Switch to Trial to change or end it.
              </Alert>
            )}
          </>
        )}

        <Field label="Notes" hint="For the Edvora team.">
          <Textarea rows={2} maxLength={2000} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
        {billing?.stripeCustomerId && <p className="text-xs text-slate-500">Stripe customer {billing.stripeCustomerId}</p>}
      </div>
    </Modal>
  );
}
